import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/content";
import { typeMeta } from "@/lib/content";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const total = heroSlides.length;

  const go = useCallback(
    (next: number) => {
      setIndex((next + total) % total);
    },
    [total]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 6500);
    return () => clearInterval(timer);
  }, [total]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta > 0 ? index - 1 : index + 1);
    }
  };

  const active = heroSlides[index];
  const activeMeta = typeMeta[active.type];

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden md:h-[660px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 焦点图`}
            title={`${slide.title} - 家庭教师动画平台`}
            className={`h-full w-full object-cover ${i === index ? "animate-soft-zoom" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/55 to-primary-950/80" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-center justify-center px-4 md:px-8">
        <div key={active.id} className="mx-auto max-w-3xl text-center animate-fade-rise">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-4 py-1.5 text-xs font-medium text-background-50">
            <i className={activeMeta.icon} aria-hidden="true" />
            {active.kicker}
          </span>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-background-50/85 md:text-base">
            {active.description}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to={`/detail/${active.type}/${active.id}`}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 sm:w-auto whitespace-nowrap"
            >
              <i className="ri-book-open-line" aria-hidden="true" />
              阅读本期内容
            </Link>
            <Link
              to={activeMeta.path}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 sm:w-auto whitespace-nowrap"
            >
              浏览{activeMeta.short}
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 md:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
      </button>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张焦点图`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}